import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, XCircle, Clock } from 'lucide-react';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | timeout | error
  const [info, setInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setState('error'); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === 'paid') { setState('paid'); return; }
      if (data.payment_status === 'failed' || data.status === 'expired') { setState('error'); return; }
      setAttempts((a) => a + 1);
    } catch { setAttempts((a) => a + 1); }
  }, [sessionId]);

  useEffect(() => {
    if (state !== 'polling') return;
    if (attempts >= 10) { setState('timeout'); return; }
    const t = setTimeout(poll, attempts === 0 ? 300 : 2000);
    return () => clearTimeout(t);
  }, [attempts, state, poll]);

  const icons = { polling: <Loader2 className="h-12 w-12 text-brand-orange animate-spin" />, paid: <CheckCircle2 className="h-12 w-12 text-emerald-600" />, timeout: <Clock className="h-12 w-12 text-amber-500" />, error: <XCircle className="h-12 w-12 text-red-600" /> };
  const titles = { polling: 'Confirmando tu pago…', paid: '¡Suscripción activada!', timeout: 'Pago en verificación', error: 'No pudimos confirmar el pago' };
  const texts = {
    polling: 'Estamos confirmando el pago con Stripe. Esto toma unos segundos.',
    paid: info ? `Recibimos tu pago de ${info.currency} ${Number(info.amount || 0).toFixed(2)}. Tu suscripción quedó activa.` : 'Tu suscripción quedó activa.',
    timeout: 'Tu pago puede tardar un momento en confirmarse. Vuelve a tu panel; el estado se actualizará automáticamente.',
    error: 'Si el cargo se realizó, se reflejará en unos minutos. Puedes reintentar desde tu panel.',
  };

  return (
    <main className="min-h-screen grid place-items-center bg-background px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 text-center card-shadow space-y-4">
        <div className="flex justify-center" data-testid={`payment-state-${state}`}>{icons[state]}</div>
        <h1 className="text-xl font-bold" data-testid="payment-title">{titles[state]}</h1>
        <p className="text-sm text-muted-foreground" data-testid="payment-text">{texts[state]}</p>
        <Button asChild className="w-full bg-brand-orange text-white hover:bg-brand-orange-dark" data-testid="payment-back"><Link to="/seller/subscription">Volver a mi suscripción</Link></Button>
      </div>
    </main>
  );
}
